import * as React from 'react';

import {
  enumPurchaseOrderStatus,
  PurchaseOrderStatus,
} from '@rematter/frontend-lib-api-client';
import { MultiSelectFilter } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtom } from 'jotai';

import { $columnSelectedStatuses } from '../../../_state/purchaseOrdersList.state';
import { useTranslations } from '../../../i18n/useTranslations';

interface StatusOption {
  id: PurchaseOrderStatus;
  label: string;
}

interface StatusColumnFilterProps {
  onClose?: () => void;
}

const getPurchaseOrderStatusColorClass = (
  status: PurchaseOrderStatus,
): string => {
  switch (status) {
    case enumPurchaseOrderStatus.Draft:
      return 'bg-neutral/40';
    case enumPurchaseOrderStatus.Open:
      return 'bg-info';
    case enumPurchaseOrderStatus.Closed:
      return 'bg-success';
    case enumPurchaseOrderStatus.Voided:
      return 'bg-failure';
    default:
      return 'bg-neutral/40';
  }
};

export const StatusColumnFilter: React.FC<StatusColumnFilterProps> = ({
  onClose,
}) => {
  const t = useTranslations();
  const [selectedStatusesSet, setSelectedStatusesSet] = useAtom(
    $columnSelectedStatuses,
  );
  const [searchQuery, setSearchQuery] = React.useState('');
  const selectedStatuses = Array.from(selectedStatusesSet);

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedStatuses),
    [selectedStatuses],
  );

  const statusOptions: StatusOption[] = React.useMemo(() => {
    const statusLabels: Record<PurchaseOrderStatus, string> = {
      [enumPurchaseOrderStatus.Draft]:
        t.filters.statusLabels.purchaseOrder.Draft,
      [enumPurchaseOrderStatus.Open]: t.filters.statusLabels.purchaseOrder.Open,
      [enumPurchaseOrderStatus.Closed]:
        t.filters.statusLabels.purchaseOrder.Closed,
      [enumPurchaseOrderStatus.Voided]:
        t.filters.statusLabels.purchaseOrder.Voided,
    };

    return Object.values(enumPurchaseOrderStatus).map((status) => ({
      id: status,
      label: statusLabels[status],
    }));
  }, [t]);

  const filteredStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [statusOptions, searchQuery],
  );

  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedStatusesSet(
        new Set(Array.from(keys) as PurchaseOrderStatus[]),
      );
    },
    [setSelectedStatusesSet],
  );

  const handleReset = React.useCallback(() => {
    setSelectedStatusesSet(new Set());
  }, [setSelectedStatusesSet]);

  const handleSearch = React.useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const getLabel = React.useCallback(
    (item: StatusOption, isSelected: boolean) => (
      <div className="flex w-full items-center gap-2">
        <div
          className={cn(
            'w-0 grow truncate text-sm',
            isSelected ? 'text-neutral' : 'text-neutral/60',
          )}
        >
          {item.label}
        </div>
        <span
          className={cn(
            'size-2 rounded-full',
            getPurchaseOrderStatusColorClass(item.id),
          )}
        />
      </div>
    ),
    [],
  );

  const selectedStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) => selectedStatuses.includes(option.id)),
    [statusOptions, selectedStatuses],
  );

  return (
    <MultiSelectFilter
      className="@StatusColumnFilter min-w-[250px]"
      entries={filteredStatusOptions}
      selectedEntries={selectedStatusOptions}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(status) => status.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.filters.searchStatuses}
      isLoading={false}
      isFetching={false}
    />
  );
};
